<script setup lang="ts">
  import { useCssModule, useSlots } from 'vue';

  defineOptions({
    name: 'll-page-content',
  });

  export interface PageContentProps {
    /**
     * The active page navigation tab. If provided, the PageContent will render a tab panel as its root element.
     */
    activeTab?: string;
  }

  const props = withDefaults(defineProps<PageContentProps>(), {
    activeTab: undefined,
  });

  const slots = useSlots();
  const classes = useCssModule();
</script>

<template>
  <div
    :id="props.activeTab ? `tabpanel-${props.activeTab}` : undefined"
    class="stash-page-content"
    :class="[
      classes.root,
      {
        [classes['stash-page-content--has-sidebar']]: !!slots.sidebar,
      },
    ]"
    data-test="stash-page-content"
    :role="props.activeTab ? 'tabpanel' : undefined"
  >
    <div :class="classes['stash-page-content__main']">
      <!-- @slot Default content -->
      <slot name="default"></slot>
    </div>

    <aside v-if="!!slots.sidebar" :class="classes['stash-page-content__sidebar']">
      <!-- @slot Sidebar content -->
      <slot name="sidebar"></slot>
    </aside>
  </div>
</template>

<style module>
  @reference "../../../styles/main.css";

  @layer utilities {
    .root {
      max-width: var(--width-container); /* 1400px */
      padding-inline: var(--grid-gutter);
      position: relative;
      margin-inline: auto;
      width: 100%;
    }

    .stash-page-content--has-sidebar {
      display: flex;
      flex-direction: column;
      gap: var(--grid-gutter);
    }

    .stash-page-content--has-sidebar .stash-page-content__main {
      flex: 1; /* 12 cols */
      min-width: 0;
    }

    .stash-page-content--has-sidebar .stash-page-content__sidebar {
      flex: 0;
      min-width: 0;
    }

    @media (width >= theme(--breakpoint-lg)) {
      .stash-page-content--has-sidebar {
        flex-direction: row;
      }

      .stash-page-content--has-sidebar .stash-page-content__main {
        flex: 3; /* 9 cols */
      }

      .stash-page-content--has-sidebar .stash-page-content__sidebar {
        flex: 1; /* 3 cols */
      }
    }
  }
</style>
